import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';

export default function PageHeader({
  title,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchId = 'pageSearch',
  actions = null,
  rightActions = null,
}) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <header className="crm-page-header is-compact !flex !flex-col !p-0 !gap-0 bg-white border-b border-slate-200 shadow-sm shrink-0 sticky top-0 z-30 select-none min-w-0">
      <div className="flex items-center justify-between gap-1.5 px-3 sm:px-4 h-12 min-h-[48px] w-full shrink-0">
        <h1 className="flex items-center text-sm font-bold text-foreground tracking-tight shrink-0">{title}</h1>

        {onSearchChange && (
          <div className="hidden sm:flex relative items-center flex-1 min-w-0 max-w-[360px] mx-4 justify-center shrink">
            <div className="relative flex items-center flex-1 max-w-[360px] w-full">
              <Search className="absolute left-2.5 size-4 text-muted-foreground pointer-events-none shrink-0" strokeWidth={1.5} />
              <Input
                type="text"
                id={searchId}
                placeholder={searchPlaceholder}
                value={searchValue || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-8 !pl-9 !pr-7 text-[14px] bg-background border-input rounded-lg w-full text-foreground placeholder:text-muted-foreground focus-visible:ring-2 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              />
              {searchValue ? (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  className="absolute right-1 size-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
                  aria-label="Clear search"
                >
                  <X className="size-3.5" strokeWidth={1.5} />
                </button>
              ) : null}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 shrink-0 ml-auto">
          {onSearchChange && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="sm:hidden size-8 shrink-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-slate-100"
              onClick={() => setMobileSearchOpen((v) => !v)}
              aria-label={`Search ${title.toLowerCase()}`}
            >
              <Search className="size-[18px]" strokeWidth={1.6} />
            </Button>
          )}
          {actions}
          {rightActions}
        </div>
      </div>

      {onSearchChange && mobileSearchOpen && (
        <div className="sm:hidden px-2 pb-2 pt-1 border-t border-slate-200 bg-white w-full animate-in slide-in-from-top-1">
          <div className="relative flex items-center w-full">
            <Search className="absolute left-2.5 size-4 text-muted-foreground pointer-events-none" strokeWidth={1.5} />
            <Input
              type="text"
              id={`${searchId}Mobile`}
              placeholder={searchPlaceholder}
              autoFocus
              value={searchValue || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-8 !pl-9 !pr-9 text-sm bg-background border-input rounded-md w-full text-foreground placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            />
            <button
              type="button"
              onClick={() => {
                if (searchValue) onSearchChange('');
                else setMobileSearchOpen(false);
              }}
              className="absolute right-1 size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
              aria-label={searchValue ? 'Clear search' : 'Close search'}
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
